/**
 * OCR taklifi qatorlari -- sof funksiya (test: ocr.test.ts). Backend `ocr_data.suggestions` driver maydonlari nomi bilan keladi;
 * bu yerda profildagi qiymat bilan solishtiriladi. Sezgir maydonlar (tug'ilgan sana, CDL raqami) ruxsati yo'qlarga
 * ko'rsatiladi, lekin qabul qilib bo'lmaydi (backend ham o'tkazib yuboradi).
 */
import type { DriverDetail } from "@/features/drivers/types";
import type { DocumentOcr } from "./types";

export const SUGGESTION_FIELDS = ["first_name", "last_name", "date_of_birth", "cdl_number", "cdl_class", "cdl_state", "cdl_expires", "medical_card_expires", "endorsements"] as const;
export type SuggestionField = (typeof SUGGESTION_FIELDS)[number];
export const SENSITIVE_FIELDS: readonly SuggestionField[] = ["date_of_birth", "cdl_number"];
export const LOW_CONFIDENCE = 0.6;

// Hujjatdagi maydon nomi (ishonch shu nom bilan keladi) -> driver maydoni
const CONFIDENCE_KEY: Partial<Record<SuggestionField, string>> = { cdl_expires: "expires_at", medical_card_expires: "expires_at" };

export interface SuggestionRow {
  field: SuggestionField;
  suggested: string;
  current: string;
  /** Hujjatdagi qiymat profildagidan farq qiladi (bo'sh maydon ham farq) */
  differs: boolean;
  /** Sezgir maydon, ruxsat yo'q -- qabul qilib bo'lmaydi */
  blocked: boolean;
  /** Ishonch past -- tekshirish kerak */
  low: boolean;
}

type DriverLike = Pick<DriverDetail, "can_edit_sensitive" | "cdl_number_masked" | "date_of_birth_masked"> & Partial<Record<SuggestionField, unknown>>;

function asText(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(", ");
  return value == null ? "" : String(value);
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

export function suggestionRows(ocr: DocumentOcr, driver: DriverLike): SuggestionRow[] {
  const s = ocr.suggestions ?? {};
  const rows: SuggestionRow[] = [];
  for (const field of SUGGESTION_FIELDS) {
    if (!(field in s)) continue;
    const suggested = asText(s[field]);
    if (!suggested) continue;
    const current = asText(driver[field]);
    const sensitive = SENSITIVE_FIELDS.includes(field);
    const blocked = sensitive && !driver.can_edit_sensitive;
    const shown = blocked ? (field === "cdl_number" ? driver.cdl_number_masked : driver.date_of_birth_masked) : current;
    const conf = ocr.confidence?.[CONFIDENCE_KEY[field] ?? field];
    rows.push({ field, suggested, current: shown, differs: norm(suggested) !== norm(current), blocked, low: conf !== undefined && conf < LOW_CONFIDENCE });
  }
  return rows;
}

/** Standart tanlov: farq qiladigan va qabul qilsa bo'ladigan maydonlar */
export function defaultPicked(rows: SuggestionRow[]): Set<SuggestionField> {
  return new Set(rows.filter((r) => r.differs && !r.blocked).map((r) => r.field));
}

export function pickedValues(rows: SuggestionRow[], picked: Set<SuggestionField>, ocr: DocumentOcr): Record<string, unknown> {
  const s = ocr.suggestions ?? {};
  return Object.fromEntries(rows.filter((r) => picked.has(r.field)).map((r) => [r.field, s[r.field]]));
}
